import {
  type Alert,
  type AlertKind,
  DEMO_REQUESTS_PER_OPENED,
  FAILED_SIGNINS_PER_HOUR,
  INVITATIONS_PER_GARAGE,
  MAIL_KINDS,
  type MailKind,
  RATE_LIMITED_PER_HOUR,
  REFUSED_PER_HOUR,
  ROUTE_GROUPS,
  type RouteGroup,
  UPLOAD_BYTES_PER_GARAGE,
} from "@bt/domain/report";
import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { CircleCheckIcon, TriangleAlertIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAdminFormat } from "../format";
import { QueryView } from "../query-view";
import { reportOf, useTRPC } from "../trpc";
import { useView } from "../view";
import { ComparedCounts, CountsFigure, Grid, ReportView, Section, SortableTable } from "./kit";
import { useAlertMessage } from "./overview";

/**
 * Is anyone misusing it? (figures.md › Misuse): the signals over the range, each section with the
 * state of its alerts now.
 */
export function MisusePage() {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const trpc = useTRPC();
  const { installation } = useView();
  const overview = useQuery(trpc.overview.queryOptions());
  const alerts = (overview.data?.alerts ?? []).filter(
    (a) => a.misuse && a.installation === installation,
  );
  const state = (kind: AlertKind, threshold: string) => (
    <QueryView query={overview} placeholder={null}>
      {() => <AlertState alert={alerts.find((a) => a.kind === kind)} threshold={threshold} />}
    </QueryView>
  );
  const garageName = useGarageNames();
  return (
    <div className="space-y-8">
      <Section title={t("adminApp.misuse.signIns")} description={t("adminApp.misuse.signInsHint")}>
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          {state(
            "failed_signins",
            t("adminApp.misuse.perHour", { count: FAILED_SIGNINS_PER_HOUR }),
          )}
          {state("rate_limited", t("adminApp.misuse.perHour", { count: RATE_LIMITED_PER_HOUR }))}
        </div>
        <Grid>
          <CountsFigure
            title={t("adminApp.misuse.failedSignIns")}
            source="signals"
            name="signin.failed"
            csvName="failed-sign-ins"
          />
          <CountsFigure
            title={t("adminApp.misuse.rateLimited")}
            description={t("adminApp.misuse.rateLimitedHint")}
            source="signals"
            name="rate_limit"
            csvName="rate-limited"
          />
          <CountsFigure
            title={t("adminApp.misuse.passwordResets")}
            source="signals"
            name="password_reset"
            csvName="password-resets"
          />
        </Grid>
      </Section>
      <Section title={t("adminApp.misuse.mail")} description={t("adminApp.misuse.mailHint")}>
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          {state(
            "invitations",
            t("adminApp.misuse.perGarageDay", { count: INVITATIONS_PER_GARAGE }),
          )}
          {state(
            "demo_requests",
            t("adminApp.misuse.timesOpened", { count: DEMO_REQUESTS_PER_OPENED }),
          )}
        </div>
        <Grid>
          <CountsFigure
            title={t("adminApp.misuse.invitations")}
            description={t("adminApp.misuse.invitationsHint")}
            source="signals"
            name="invitation.sent"
            csvName="invitations-per-garage"
            labelOf={garageName}
          />
          <ComparedCounts
            title={t("adminApp.misuse.demoRequests")}
            description={t("adminApp.misuse.demoRequestsHint")}
            lines={[
              { metric: "demo.requested", label: t("adminApp.misuse.requested") },
              { metric: "demo.opened", label: t("adminApp.misuse.opened") },
            ]}
            csvName="demo-requests"
          />
          <CountsFigure
            title={t("adminApp.misuse.mailSent")}
            source="signals"
            name="mail.sent"
            csvName="mail-sent"
            labelOf={mailKind(t)}
          />
          <CountsFigure
            title={t("adminApp.misuse.mailFailed")}
            source="signals"
            name="mail.failed"
            csvName="mail-failed"
            labelOf={mailKind(t)}
          />
        </Grid>
      </Section>
      <Section title={t("adminApp.misuse.uploads")} description={t("adminApp.misuse.uploadsHint")}>
        {state(
          "uploads",
          t("adminApp.misuse.bytesPerGarageDay", { bytes: format.bytes(UPLOAD_BYTES_PER_GARAGE) }),
        )}
        <Grid>
          <CountsFigure
            title={t("adminApp.misuse.uploadBytes")}
            source="signals"
            name="upload.bytes"
            csvName="upload-bytes-per-garage"
            labelOf={garageName}
            formatValue={format.bytes}
          />
          <CountsFigure
            title={t("adminApp.misuse.uploadsRefused")}
            description={t("adminApp.misuse.uploadsRefusedHint")}
            source="signals"
            name="upload.refused"
            csvName="uploads-refused"
          />
        </Grid>
      </Section>
      <Section title={t("adminApp.misuse.traffic")} description={t("adminApp.misuse.trafficHint")}>
        {state("refused", t("adminApp.misuse.perHour", { count: REFUSED_PER_HOUR }))}
        <Grid>
          <CountsFigure
            title={t("adminApp.misuse.unauthorized")}
            source="signals"
            name="http.401"
            csvName="http-401"
            labelOf={routeGroup(t)}
          />
          <CountsFigure
            title={t("adminApp.misuse.forbidden")}
            source="signals"
            name="http.403"
            csvName="http-403"
            labelOf={routeGroup(t)}
          />
        </Grid>
        <ReportView>
          {(report) => {
            const outliers = (report.garageTable ?? []).filter((g) =>
              g.flags?.includes("outlier:requests"),
            );
            return (
              <SortableTable
                title={t("adminApp.misuse.requestOutliers", { count: outliers.length })}
                description={t("adminApp.misuse.requestOutliersHint")}
                columns={[
                  { key: "name", label: t("adminApp.garages.name") },
                  { key: "requests30", label: t("adminApp.garages.requests"), numeric: true },
                  { key: "activeDays30", label: t("adminApp.garages.activeDays"), numeric: true },
                  { key: "members", label: t("adminApp.garages.members"), numeric: true },
                ]}
                rows={outliers.map((g) => ({
                  name: g.name ?? g.id,
                  requests30: g.requests30 ?? 0,
                  activeDays30: g.activeDays30 ?? 0,
                  members: g.members ?? 0,
                }))}
                csvName="request-outliers"
                initialSort="requests30"
              />
            );
          }}
        </ReportView>
      </Section>
    </div>
  );
}

const mailKind = (t: TFunction) => (value: string) =>
  (MAIL_KINDS as readonly string[]).includes(value)
    ? t(`adminApp.misuse.mailKinds.${value as MailKind}`)
    : value;

const routeGroup = (t: TFunction) => (value: string) =>
  (ROUTE_GROUPS as readonly string[]).includes(value)
    ? t(`adminApp.routeGroups.${value as RouteGroup}`)
    : value;

/** Garage ids as names, from the latest report's garage table; an id without one stays an id. */
function useGarageNames() {
  const trpc = useTRPC();
  const { installation } = useView();
  const query = useQuery(trpc.installation.get.queryOptions({ name: installation }));
  const table = query.data ? reportOf(query.data)?.garageTable : undefined;
  return (id: string) => table?.find((g) => g.id === id)?.name ?? id;
}

/** Whether an alert is raised now, with icon and text; the threshold is said either way. */
function AlertState({ alert, threshold }: { alert: Alert | undefined; threshold: string }) {
  const { t } = useTranslation();
  const message = useAlertMessage();
  return alert ? (
    <p className="flex items-center gap-1.5 text-sm font-medium text-partial-fg">
      <TriangleAlertIcon className="size-4 shrink-0" aria-hidden />
      {message(alert)}
    </p>
  ) : (
    <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <CircleCheckIcon className="size-4 shrink-0 text-ok-fg" aria-hidden />
      {t("adminApp.misuse.noAlert", { threshold })}
    </p>
  );
}
